import React from "react";
import { Box } from "@mui/material";
import {
  certificateFontFamily,
  certificateFontKind,
  type CertificateOrientation,
  type CertificateTemplate,
} from "../../../domain/certificates/certificateTemplates";
import {
  Achievement,
  certificateInk,
  Footer,
  Intro,
  OrganisationMark,
  RecipientName,
  type CertificateDesign,
} from "./certificateParts";

const GOLD = "#f6b51e";
const GOLD_SHADE = "#e09a0c";
const GOLD_LIGHT = "#ffd45c";
const RAY = "#f7c948";

/**
 * The title, large and in sentence case, on two lines: its last word in the
 * primary colour, the words before it in the ink colour. One element, so it
 * still reads as one title.
 */
const TwoToneTitle: React.FC<{ template: CertificateTemplate; title: string }> = ({
  template,
  title,
}) => {
  const words = title.trim().split(/\s+/);
  const last = words.pop() ?? "";
  // Script and blackletter ship one weight; a bold would be faked.
  const oneWeight = ["script", "blackletter"].includes(certificateFontKind(template.font));
  return (
    <Box
      sx={{
        fontFamily: certificateFontFamily(template.font),
        fontSize: 76,
        fontWeight: oneWeight ? 400 : 700,
        lineHeight: 1.02,
        letterSpacing: oneWeight ? 0 : "-1px",
        color: certificateInk(template).ink,
        mb: "6px",
      }}
    >
      {words.length > 0 && `${words.join(" ")} `}
      <Box component="span" sx={{ display: "block", color: template.primaryColour }}>
        {last}
      </Box>
    </Box>
  );
};

/** A short brush stroke under the title. */
const BrushStroke: React.FC<{ colour: string }> = ({ colour }) => (
  <Box
    component="svg"
    viewBox="0 0 160 16"
    aria-hidden="true"
    sx={{ width: 160, height: 16, mb: "28px" }}
  >
    <path
      d="M4,12 C40,4 100,2 156,5"
      fill="none"
      stroke={colour}
      strokeWidth={6}
      strokeLinecap="round"
    />
  </Box>
);

/**
 * A gold trophy on a stack of two books, with sparks round it, on soft blobs.
 * Drawn in a 460 × 794 box that sits against the page's right edge.
 */
const TrophyArt: React.FC<{ primary: string; accent: string }> = ({
  primary,
  accent,
}) => (
  <>
    {/* Blobs, palest at the back. */}
    <path
      d="M120,0 C110,60 170,150 290,150 C380,150 430,110 460,60 L460,0 Z"
      fill={accent}
      opacity={0.3}
    />
    <path
      d="M240,0 C230,70 300,120 380,110 C420,104 445,90 460,80 L460,0 Z"
      fill={accent}
      opacity={0.35}
    />
    <path
      d="M150,200 C230,140 380,120 460,150 L460,640 C380,660 240,640 190,560
         C130,480 60,500 50,420 C40,330 90,250 150,200 Z"
      fill={accent}
      opacity={0.75}
    />
    <path
      d="M200,140 C300,120 420,150 460,180 L460,420 C400,380 300,360 230,300
         C180,256 150,180 200,140 Z"
      fill={primary}
      opacity={0.55}
    />
    <path
      d="M60,520 C120,480 200,520 230,600 C250,650 210,690 150,660 C100,636 30,570 60,520 Z"
      fill={primary}
      opacity={0.45}
    />
    <circle cx={120} cy={170} r={30} fill={primary} />

    {/* Books, running off the page edge. */}
    <g>
      <rect x={232} y={636} width={240} height={46} rx={6} fill="#f2c94c" />
      <rect x={232} y={640} width={240} height={10} fill="#ffffff" opacity={0.7} />
      <rect x={210} y={580} width={262} height={56} rx={6} fill={primary} />
      <rect x={218} y={594} width={254} height={30} fill="#ffffff" opacity={0.85} />
      <path d="M218,600 L472,600 M218,608 L472,608 M218,616 L472,616" stroke="#d9e2e6" strokeWidth={2} />
    </g>

    {/* The trophy. */}
    <g transform="translate(316 0)">
      {/* Base. */}
      <ellipse cx={0} cy={576} rx={66} ry={10} fill="#000000" opacity={0.15} />
      <rect x={-64} y={530} width={128} height={44} rx={10} fill="#e3c08d" />
      <rect x={-64} y={530} width={128} height={12} rx={6} fill="#f0d4a8" />
      {/* Stem. */}
      <path d="M-26,532 C-22,500 -14,480 -12,450 L12,450 C14,480 22,500 26,532 Z" fill={GOLD_SHADE} />
      <path d="M-20,532 C-16,500 -10,480 -8,450 L0,450 L0,532 Z" fill={GOLD} />
      {/* Handles. */}
      <path
        d="M-78,288 C-140,288 -140,400 -70,410 M78,288 C140,288 140,400 70,410"
        fill="none"
        stroke={GOLD}
        strokeWidth={18}
        strokeLinecap="round"
      />
      {/* Cup. */}
      <path d="M-92,262 L92,262 C92,380 50,450 0,456 C-50,450 -92,380 -92,262 Z" fill={GOLD} />
      <path d="M30,262 L92,262 C92,380 50,450 0,456 C40,430 56,360 30,262 Z" fill={GOLD_SHADE} opacity={0.6} />
      <path d="M-70,280 C-72,340 -54,390 -30,420" fill="none" stroke={GOLD_LIGHT} strokeWidth={10} strokeLinecap="round" opacity={0.8} />
      <ellipse cx={0} cy={262} rx={92} ry={14} fill={GOLD_SHADE} />
      <ellipse cx={0} cy={262} rx={80} ry={9} fill="#c98606" />
      {/* Star. */}
      <polygon
        points="0,-34 10,-11 34,-10 15,6 22,30 0,17 -22,30 -15,6 -34,-10 -10,-11"
        transform="translate(4 346)"
        fill="#fff8ec"
      />
    </g>

    {/* Sparks round the cup. */}
    {[
      [176, 236, 214, 260],
      [158, 340, 200, 334],
      [176, 432, 206, 410],
      [262, 168, 278, 206],
      [384, 186, 362, 216],
      [420, 216, 392, 232],
    ].map(([x1, y1, x2, y2]) => (
      <line
        key={`${x1},${y1}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={RAY}
        strokeWidth={9}
        strokeLinecap="round"
      />
    ))}
  </>
);

/**
 * Where the art sits and the text column's inset, which keeps clear of it.
 * On a portrait page the art is smaller, in the top-right corner, with the
 * text below it.
 */
const PLACEMENT: Record<
  CertificateOrientation,
  { artHeight: number; textInset: string }
> = {
  landscape: { artHeight: 794, textInset: "72px 400px 56px 72px" },
  portrait: { artHeight: 440, textInset: "330px 72px 64px 72px" },
};

/**
 * Champion: a big two-tone title on the left and a gold trophy on a stack of
 * books on the right, on soft blobs of colour. The text is left-aligned.
 */
export const championDesign: CertificateDesign = {
  bodyFont: "montserrat",
  Layout: ({ template, text, orientation }) => {
    const { primaryColour: primary, accentColour: accent } = template;
    const placement = PLACEMENT[orientation];
    return (
      <>
        {/* A pale blob in the bottom-left corner. */}
        <Box
          component="svg"
          viewBox="0 0 300 200"
          aria-hidden="true"
          sx={{ position: "absolute", left: 0, bottom: 0, width: 300, height: 200 }}
        >
          <path
            d="M0,40 C70,30 150,70 190,130 C210,160 220,185 226,200 L0,200 Z"
            fill={accent}
            opacity={0.3}
          />
          <path d="M0,130 C80,120 150,160 170,200 L0,200 Z" fill={accent} opacity={0.35} />
        </Box>
        {/* A pale blob and a dot in the bottom-right corner. */}
        <Box
          component="svg"
          viewBox="0 0 150 104"
          aria-hidden="true"
          sx={{ position: "absolute", right: 0, bottom: 0, width: 150, height: 104 }}
        >
          <path
            d="M0,104 C-10,40 50,0 110,0 C130,0 142,4 150,8 L150,104 Z"
            fill={accent}
            opacity={0.5}
          />
          <circle cx={82} cy={82} r={13} fill={primary} />
        </Box>
        <Box
          component="svg"
          viewBox="0 0 460 794"
          aria-hidden="true"
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            height: placement.artHeight,
            aspectRatio: "460 / 794",
          }}
        >
          <TrophyArt primary={primary} accent={accent} />
        </Box>
        <Box
          sx={{
            position: "absolute",
            inset: placement.textInset,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            textAlign: "left",
          }}
        >
          <OrganisationMark template={template} align="left" />
          <TwoToneTitle template={template} title={text.certificateTitle} />
          <BrushStroke colour={accent} />
          <Intro template={template} />
          <RecipientName template={template} name={text.recipientName} align="left" />
          <Achievement template={template} text={text} orientation={orientation} />
          <Box sx={{ flex: 1 }} />
          <Footer template={template} text={text} orientation={orientation} />
        </Box>
      </>
    );
  },
};
